/**
 * One checklist item and the exact file it will send (R3 T6a): the file's
 * name and meta line, its checks, and "Change file" / "Download".
 */

import { Check, ChevronDown, Download, Pin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import { SubmissionFileChecks } from '@/components/pack/SubmissionFileChecks'
import { fileMetaLine } from '@/lib/submission-files-text'
import type { SubmissionFileItem } from '@/types/submission-files'

const MIME_OF_FORMAT: Record<string, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

interface SubmissionFileRowProps {
  item: SubmissionFileItem
  readOnly: boolean
  isBusy?: boolean
  onPin: (outputVersionId: string | null) => void
  onDownload: () => void
}

export function SubmissionFileRow({ item, readOnly, isBusy = false, onPin, onDownload }: SubmissionFileRowProps) {
  const { file } = item
  const missing = item.checks.find((c) => c.key === 'file' && c.status === 'attention')
  const canChange = !readOnly && item.choices.length > 0 && (item.choices.length > 1 || !!file?.pinned)

  return (
    <li className="group py-3" data-item-id={item.itemId}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm">{item.title}</p>
          {file ? (
            <div className="mt-1 flex min-w-0 items-center gap-1.5">
              <FileTypeIcon
                variant="glyph"
                size={14}
                mimeType={MIME_OF_FORMAT[file.format] ?? null}
                fileName={file.name}
                className="shrink-0"
              />
              <span className="truncate text-meta text-foreground" title={file.name}>
                {file.name}
              </span>
            </div>
          ) : null}
          {file ? (
            <p className="mt-0.5 flex items-center gap-1 text-meta">
              {fileMetaLine(file)}
              {file.pinned && (
                <>
                  {' · '}
                  <span className="inline-flex items-center gap-0.5">
                    <Pin aria-hidden className="h-3 w-3 text-muted-foreground" />
                    Chosen by you
                  </span>
                </>
              )}
            </p>
          ) : (
            <p className="mt-1 text-meta text-foreground">{missing?.label ?? 'No file yet'}</p>
          )}
          {file && <SubmissionFileChecks checks={item.checks} />}
        </div>

        {file && (
          <div className="flex shrink-0 items-center gap-1">
            {canChange && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" disabled={isBusy}>
                    Change file
                    <ChevronDown aria-hidden className="ml-1 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  {item.choices.map((choice) => (
                    <DropdownMenuItem
                      key={choice.outputVersionId}
                      onSelect={() => {
                        if (!choice.selected) onPin(choice.outputVersionId)
                      }}
                      className="items-start gap-2"
                    >
                      <Check
                        aria-hidden
                        className={choice.selected ? 'mt-0.5 h-4 w-4 shrink-0' : 'mt-0.5 h-4 w-4 shrink-0 opacity-0'}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm">{choice.name}</span>
                        <span className="block text-meta">{fileMetaLine(choice)}</span>
                      </span>
                    </DropdownMenuItem>
                  ))}
                  {file.pinned && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => onPin(null)}>Always use the newest file</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="ghost" size="sm" onClick={onDownload} aria-label={`Download ${file.name}`}>
              <Download aria-hidden className="mr-1 h-4 w-4" />
              Download
            </Button>
          </div>
        )}
      </div>
    </li>
  )
}
